import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { requestPasswordReset } from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import logo from "@/assets/famibelleza_logo.png";

export default function ForgotPassword() {
  const { t } = useTranslation();
  usePageTitle(t("forgotPassword.title"));
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  };

  return (
    <div className="relative flex items-center justify-center py-12 px-4 overflow-hidden">
      <img src={logo} alt="" className="absolute top-0 w-1/2 h-full object-contain z-0 opacity-10 blur-md" />
      <div className="relative z-10 max-w-md w-full bg-white rounded-2xl shadow-md p-8 space-y-6 text-center">
        {sent ? (
          <>
            <h1 className="text-3xl font-extrabold text-brand">{t("forgotPassword.sentTitle")}</h1>
            <p className="text-gray-600">{t("forgotPassword.sentDescription", { email })}</p>
            <Link to="/login" className="text-sm font-medium text-brand hover:text-brand/80">{t("common.backToLogin")}</Link>
          </>
        ) : (
          <>
            <div>
              <h1 className="text-3xl font-extrabold text-brand">{t("forgotPassword.title")}</h1>
              <p className="mt-2 text-sm text-gray-600">{t("forgotPassword.description")}</p>
            </div>

            <form onSubmit={onSubmit} className="flex flex-col gap-4 text-left">
              <div className="flex flex-col gap-1">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">{t("common.email")}</label>
                <input
                  id="email"
                  type="email"
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 px-4 bg-brand text-white font-semibold rounded-2xl cursor-pointer hover:bg-brand/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {submitting ? t("forgotPassword.submitting") : t("forgotPassword.submit")}
              </button>
            </form>

            <Link to="/login" className="block text-sm font-medium text-brand hover:text-brand/80">{t("common.backToLogin")}</Link>
          </>
        )}
      </div>
    </div>
  );
}
